import { createModal } from './modal.js';
import { authenticate, checkEmail, exchangeToken, fetchProfile, revoke } from './hubid.js';
import { BAKED } from './config.js';

// Hands the verified token to the host site so it can build its own session.
// Returns { ok, message }.
async function bridgeSession(config, token) {
  const endpoint = config.sessionEndpoint;
  if (!endpoint || endpoint === 'none') return { ok: true };

  const headers = { 'Content-Type': 'application/json' };
  const csrf = document.querySelector('meta[name="csrf-token"]');
  if (csrf) headers['X-CSRF-TOKEN'] = csrf.getAttribute('content');

  try {
    const res = await fetch(endpoint, {
      method: 'POST',
      headers,
      credentials: 'same-origin',
      body: JSON.stringify({ token }),
    });
    // No such route -> this site has no server session to start. Treat it as a
    // client-side-only login rather than failing, but say so loudly: on a site
    // that *does* need a session, this is almost always a wrong path.
    if (res.status === 404 || res.status === 405) {
      console.warn(
        `[HubLogin] No session endpoint at "${endpoint}" (HTTP ${res.status}). ` +
          'Signed in client-side only — this site will NOT have a server session. ' +
          'Add the endpoint, fix data-session-endpoint, or set it to "none" to silence this.'
      );
      return { ok: true };
    }

    const data = await res.json().catch(() => null);
    if (res.ok && data && data.success) return { ok: true };
    return { ok: false, message: (data && data.message) || 'Could not start your session on this site.' };
  } catch (_) {
    return { ok: false, message: 'Could not start your session on this site.' };
  }
}

// Hub Culture brand theme — used unless the site overrides individual keys.
const HUBCULTURE_THEME = {
  title: 'Sign in to Hub Culture',
  subtitle: 'Log in with your HubID email and password.',
  logo: 'https://hubculture.com/images/hub_new_logo.png',
  primaryColor: '#16407C',
  primaryColorHover: '#0f2f5e',
  accentColor: '#60BF93',
  registerUrl: 'https://hubculture.com/register',
  forgotUrl: 'https://hubculture.com/account/forgotpassword/',
};

const DEFAULTS = {
  // NOTE: keys defined below override BAKED — do not redeclare anything BAKED
  // already sets (baseUrl, publicKey, privateKey, sessionEndpoint, selector,
  // logoutSelector, redirect, legacy*), or the baked value is silently lost.
  ...BAKED,
  clientId: null,
  tokenStorageKey: 'hubid-token', // localStorage key; set null to disable persistence
  exchangeToken: true, // PUT /token to swap the /auth token for the long-lived one (matches Ven)
  fetchProfile: true, // GET /user after a successful /auth
  theme: {},
  onSuccess: null, // (user, token) => {}
  onError: null, // (error) => {}
  onOpen: null,
  onClose: null,
};

let config = null;
let modal = null;
let delegatedHandler = null;
let baseline = {}; // the site-level config, so per-element overrides can reset to it
let scriptDefaults = {}; // data-* attributes from the <script> tag
let initialised = false;

// Called once at load with the script tag's data-* config. These act as
// defaults *under* whatever the site passes to init(), so the two can be used
// together and in either order.
export function setScriptDefaults(defaults = {}) {
  scriptDefaults = defaults || {};
}

export function wasInitialised() {
  return initialised;
}

export function init(options = {}) {
  initialised = true;
  baseline = {
    ...scriptDefaults,
    ...options,
    theme: { ...(scriptDefaults.theme || {}), ...(options.theme || {}) },
  };
  return applyConfig(baseline);
}

function applyConfig(options = {}) {
  // Note: use != null (not ||) so an intentional empty string (same-origin) is honored
  const rawBaseUrl = options.baseUrl != null ? options.baseUrl : DEFAULTS.baseUrl;
  config = {
    ...DEFAULTS,
    ...options,
    baseUrl: rawBaseUrl.replace(/\/+$/, ''),
    theme: { ...HUBCULTURE_THEME, ...(options.theme || {}) },
  };

  if (!config.clientId) {
    console.warn(
      '[HubLogin] No client-id set. The email pre-check (/user/checkEmail) only ' +
        'allows the origin registered for a client_id, so it will be skipped on ' +
        'this site. Add data-client-id="<your HubID client id>".'
    );
  }

  if (modal) modal.destroy();
  modal = createModal({
    theme: config.theme,
    onCheckEmail: handleCheckEmail,
    onSubmit: handleSubmit,
    onClose: () => config.onClose && config.onClose(),
  });

  if (delegatedHandler) document.removeEventListener('click', delegatedHandler);
  delegatedHandler = (event) => {
    if (!event.target || !event.target.closest) return;

    // Site's own logout link: drop our token, but let the click proceed.
    if (config.logoutSelector && event.target.closest(config.logoutSelector)) {
      clearStoredToken();
      return;
    }

    const trigger = event.target.closest(config.selector);
    if (!trigger) return;
    event.preventDefault();
    open();
  };
  document.addEventListener('click', delegatedHandler);
}

// Merge extra options into the site-level config (e.g. adding an onSuccess).
export function configure(options = {}) {
  return init({
    ...baseline,
    ...options,
    theme: { ...(baseline.theme || {}), ...(options.theme || {}) },
  });
}

// Applies one <hub-login> element's attributes on top of the site-level
// baseline. Called on every click so a bare element resets to the baseline
// instead of inheriting whatever the previously-clicked element set.
export function applyElementConfig(overrides = {}) {
  return applyConfig({
    ...baseline,
    ...overrides,
    theme: { ...(baseline.theme || {}), ...(overrides.theme || {}) },
  });
}

export function isReady() {
  return !!modal;
}

export function open() {
  // Self-initialise if needed: auto-init waits for DOMContentLoaded, so a site
  // calling HubLogin.open() from its own early script shouldn't have to care.
  if (!modal) {
    if (!document.body) {
      document.addEventListener('DOMContentLoaded', () => open(), { once: true });
      return;
    }
    applyConfig(baseline);
  }
  modal.reset();
  modal.open();
  if (config.onOpen) config.onOpen();
}

export function close() {
  if (modal) modal.close();
}

export function getToken() {
  if (!config || !config.tokenStorageKey) return null;
  try {
    return window.localStorage.getItem(config.tokenStorageKey);
  } catch (_) {
    return null;
  }
}

export function isAuthenticated() {
  return !!getToken();
}

function storeToken(token) {
  if (!config.tokenStorageKey) return;
  try {
    window.localStorage.setItem(config.tokenStorageKey, token);
  } catch (_) {
    // storage unavailable (private mode / disabled) — token still returned via callback
  }
}

function setCookie(name, value, days) {
  const bits = [`${name}=${value}`, 'path=/', 'SameSite=Lax'];
  if (days != null) {
    bits.push(`expires=${new Date(Date.now() + days * 864e5).toUTCString()}`);
  }
  if (window.location.protocol === 'https:') bits.push('Secure');
  document.cookie = bits.join('; ');
}

// Mirrors the token/profile into the places existing Hub code already reads.
// See the compatibility notes in config.js for who consumes each one.
function writeCompatStorage(token, user) {
  try {
    if (config.legacyTokenKey) window.localStorage.setItem(config.legacyTokenKey, token);
    if (config.legacyUserKey && user) {
      window.localStorage.setItem(config.legacyUserKey, JSON.stringify(user));
    }
  } catch (_) {
    /* storage unavailable */
  }

  const userId = user && (user.id != null ? user.id : user.user_id);
  if (config.legacyUserCookie && userId != null) {
    const value = config.legacyCookieSuffix ? `${userId}-${config.legacyCookieSuffix}` : String(userId);
    setCookie(config.legacyUserCookie, value, 1);
  }
}

function clearStoredToken() {
  if (!config) return;
  const keys = [config.tokenStorageKey, config.legacyTokenKey, config.legacyUserKey];
  keys.forEach((key) => {
    if (!key) return;
    try {
      window.localStorage.removeItem(key);
    } catch (_) {
      /* noop */
    }
  });
  if (config.legacyUserCookie) setCookie(config.legacyUserCookie, '', -1);
}

export async function logout() {
  const token = getToken();
  await revoke(config, token);
  clearStoredToken();
}

// Step 1: does this account exist? Mirrors hubculture.com, which refuses to
// show the password field until the email resolves to a real account.
async function handleCheckEmail({ email }) {
  if (!email) return;
  modal.setLoading(true);
  modal.setError(null);
  try {
    const { exists } = await checkEmail(config, email);
    if (exists) {
      modal.setStep(2);
      return;
    }
    const register = config.theme && config.theme.registerUrl;
    modal.setError(
      register
        ? `User does not exist. You can <a href="${register}" target="_blank" rel="noopener">REGISTER</a> your account.`
        : 'User does not exist.',
      !!register
    );
  } catch (err) {
    // Transport/CORS failure (status 0) means we couldn't ask, not that the
    // account is missing — usually a client_id whose registered origin doesn't
    // match this site. Fail open to the password step: /auth is the real gate,
    // and it allows any origin. A hard block here would make login impossible.
    if (err.status === 0) {
      console.warn(
        '[HubLogin] Email pre-check unavailable — continuing to the password step. ' +
          'Set data-client-id to this site\'s registered HubID client to enable it.'
      );
      modal.setStep(2);
    } else {
      modal.setError(err.message || 'Could not check that email.');
      if (config.onError) config.onError(err);
    }
  } finally {
    modal.setLoading(false);
  }
}

async function handleSubmit({ email, password }) {
  modal.setLoading(true);
  modal.setError(null);
  try {
    const auth = await authenticate(config, email, password);

    // Mirror Ven: swap the /auth access token for the long-lived token before use.
    let token = auth.token;
    if (config.exchangeToken) {
      const exchanged = await exchangeToken(config, token);
      if (exchanged) token = exchanged;
    }
    storeToken(token);

    let user = auth.profile;
    if (config.fetchProfile) {
      const fetched = await fetchProfile(config, token);
      if (fetched) user = fetched;
    }

    // Written before the session handoff/reload so existing Hub code (Ven's Vue
    // store, the chat widget) finds the token on the very next page load.
    writeCompatStorage(token, user);

    // Hand off to the site so its own session is created. Keep the modal open
    // and show why if this fails — the user is signed in to HubID but not to
    // this site, and silently "succeeding" would be misleading.
    const bridged = await bridgeSession(config, token);
    if (!bridged.ok) {
      modal.setError(bridged.message);
      if (config.onError) config.onError(new Error(bridged.message));
      return;
    }

    modal.close();
    if (config.onSuccess) config.onSuccess(user, token);

    // Send the user on. replace() rather than assign() so the back button
    // doesn't return them to the page they logged in from.
    const destination = config.redirect;
    if (destination && destination !== 'none' && destination !== 'false') {
      window.location.replace(destination);
    }
  } catch (err) {
    modal.setError(err.message || 'Something went wrong. Please try again.');
    if (config.onError) config.onError(err);
  } finally {
    modal.setLoading(false);
  }
}
